import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, Button, Badge, Spinner, Modal, Select } from "@/components/ui";
import Markdown from "@/components/Markdown";
import MessageThread from "@/components/MessageThread";
import { money } from "@/lib/utils";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, FileText, Phone, Mail, MapPin, Pill, AlertCircle,
  Home, Utensils, Car, ClipboardList, HeartPulse, Receipt, ShieldCheck,
} from "lucide-react";

const TABS = ["Overview", "Assessments", "Care Plan", "Claims", "Authorizations", "Letters", "Messages"];
const LETTER_TYPES = ["Welcome / Onboarding", "Care Plan Notification", "Missed Appointment Outreach", "Prior Authorization Approval", "Prior Authorization Denial (with appeal rights)", "Annual Wellness Reminder"];

export default function MemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [tab, setTab] = useState("Overview");

  const [summary, setSummary] = useState("");
  const [summaryBusy, setSummaryBusy] = useState(false);

  const [carePlan, setCarePlan] = useState("");
  const [cpBusy, setCpBusy] = useState(false);

  const [letterOpen, setLetterOpen] = useState(false);
  const [letterType, setLetterType] = useState(LETTER_TYPES[0]);
  const [letter, setLetter] = useState("");
  const [letterBusy, setLetterBusy] = useState(false);

  useEffect(() => { api.get(`/members/${id}`).then((r) => setM(r.data)); }, [id]);

  const genSummary = async () => {
    setSummaryBusy(true); setSummary("");
    try {
      const { data } = await api.post("/ai/summary", { member_id: id });
      setSummary(data.content);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSummaryBusy(false); }
  };

  const reload = () => api.get(`/members/${id}`).then((r) => setM(r.data));

  const genCarePlan = async () => {
    setCpBusy(true); setCarePlan("");
    try {
      const { data } = await api.post("/ai/care-plan", { member_id: id });
      setCarePlan(data.content);
      toast.success("AI care plan draft generated & saved to chart");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCpBusy(false); }
  };

  const genLetter = async () => {
    setLetterBusy(true); setLetter("");
    try {
      const { data } = await api.post("/ai/letter", { member_id: id, letter_type: letterType });
      setLetter(data.content);
      toast.success("Letter generated & saved to chart");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLetterBusy(false); }
  };

  if (!m) return <Spinner label="Loading member chart…" />;

  return (
    <div className="vc-fade-up">
      <button onClick={() => navigate("/members")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-members">
        <ArrowLeft className="h-4 w-4" /> Member Registry
      </button>

      {/* Header */}
      <Card className="p-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <img src={m.photo_url} alt="" className="h-20 w-20 rounded-xl object-cover bg-muted" />
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-display text-2xl font-bold tracking-tight">{m.first_name} {m.last_name}</h1>
              <Badge tone={m.risk_level}>{m.risk_level} Risk · {m.risk_score}</Badge>
              <Badge tone={m.status}>{m.status}</Badge>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
              <span className="font-mono">MRN {m.mrn}</span>
              <span>DOB {m.dob}</span>
              <span>{m.gender}</span>
              <span>{m.plan}</span>
              <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{m.phone}</span>
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{m.county} County</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {m.domains.map((d) => <span key={d} className="text-xs rounded bg-accent px-2 py-0.5 text-accent-foreground font-medium">{d}</span>)}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button onClick={genSummary} disabled={summaryBusy} data-testid="ai-summary-btn">
              <Sparkles className="h-4 w-4" /> {summaryBusy ? "Analyzing…" : "AI Clinical Snapshot"}
            </Button>
            <Button variant="outline" onClick={() => setLetterOpen(true)} data-testid="generate-letter-btn">
              <FileText className="h-4 w-4" /> Generate Letter
            </Button>
          </div>
        </div>

        {(summary || summaryBusy) && (
          <div className="mt-5 rounded-lg border border-brand-200 bg-brand-50 p-4">
            <div className="flex items-center gap-2 mb-1 text-brand-800">
              <Sparkles className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">AI Clinical Snapshot</span>
            </div>
            {summaryBusy ? <Spinner label="Generating snapshot…" /> : <Markdown>{summary}</Markdown>}
          </div>
        )}
      </Card>

      {/* Care manager row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Card className="p-4"><p className="text-xs text-muted-foreground">Care Manager</p><p className="font-semibold mt-1">{m.care_manager_name}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Enrolled</p><p className="font-semibold mt-1">{m.enrollment_date}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Open Care Gaps</p><p className="font-semibold mt-1 text-amber-700">{m.care_gaps.length}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Active Care Plans</p><p className="font-semibold mt-1">{m.care_plans.length}</p></Card>
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-border flex gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase().replace(/ /g, "-")}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === t ? "border-brand-700 text-brand-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "Overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-5">
              <h3 className="font-display font-semibold flex items-center gap-2 mb-3"><HeartPulse className="h-4 w-4 text-brand-700" /> Conditions</h3>
              <div className="space-y-1.5">{m.conditions.length ? m.conditions.map((c) => <p key={c} className="text-sm text-muted-foreground">• {c}</p>) : <p className="text-sm text-muted-foreground">None documented</p>}</div>
            </Card>
            <Card className="p-5">
              <h3 className="font-display font-semibold flex items-center gap-2 mb-3"><Pill className="h-4 w-4 text-brand-700" /> Medications</h3>
              <div className="space-y-2">{m.medications.length ? m.medications.map((md, i) => <div key={i} className="text-sm"><p className="font-medium">{md.name}</p><p className="text-xs text-muted-foreground">{md.sig}</p></div>) : <p className="text-sm text-muted-foreground">None</p>}</div>
            </Card>
            <Card className="p-5">
              <h3 className="font-display font-semibold flex items-center gap-2 mb-3"><AlertCircle className="h-4 w-4 text-amber-600" /> Open Care Gaps</h3>
              <div className="space-y-1.5">{m.care_gaps.length ? m.care_gaps.map((g) => <p key={g} className="text-sm text-amber-800 bg-amber-50 rounded px-2 py-1">{g}</p>) : <p className="text-sm text-muted-foreground">No open gaps</p>}</div>
            </Card>
            <Card className="p-5 lg:col-span-3">
              <h3 className="font-display font-semibold mb-3">Health-Related Social Needs (HRSN)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[["housing", Home, "Housing"], ["food_security", Utensils, "Food Security"], ["transportation", Car, "Transportation"]].map(([k, Icon, label]) => (
                  <div key={k} className="flex items-center gap-3 rounded-md border border-border p-3">
                    <Icon className="h-5 w-5 text-brand-700" />
                    <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-sm font-medium">{m.hrsn?.[k] || "—"}</p></div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {tab === "Assessments" && (
          <Card className="overflow-hidden">
            <TableList
              cols={["Assessment", "Status", "Score", "Flag", "Assessor", "Completed"]}
              rows={m.assessments.map((a) => [a.type, <Badge tone={a.status}>{a.status}</Badge>, a.score ?? "—", a.risk_flag || "—", a.assessor, a.completed_date || "—"])}
              empty="No assessments on file"
              icon={ClipboardList}
            />
          </Card>
        )}

        {tab === "Care Plan" && (
          <div className="space-y-4">
            <Card className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display font-semibold text-lg">Person-Centered Care Plan</h3>
                  <p className="text-sm text-muted-foreground">Draft an evidence-based plan with AI, then review and finalize.</p>
                </div>
                <Button onClick={genCarePlan} disabled={cpBusy} data-testid="ai-careplan-btn"><Sparkles className="h-4 w-4" />{cpBusy ? "Drafting…" : "Draft with AI"}</Button>
              </div>
              {(carePlan || cpBusy) && (
                <div className="mt-4 rounded-lg border border-brand-200 bg-brand-50 p-5">
                  {cpBusy ? <Spinner label="Drafting person-centered care plan…" /> : <Markdown>{carePlan}</Markdown>}
                </div>
              )}
            </Card>
            {m.care_plans.map((cp) => (
              <Card key={cp.id} className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold flex items-center gap-2">{cp.ai_generated && <Sparkles className="h-4 w-4 text-brand-700" />}{cp.ai_generated ? "AI-Generated Care Plan" : "Existing Care Plan"}</h4>
                  <Badge tone={cp.status}>{cp.status}</Badge>
                </div>
                {cp.narrative ? (
                  <Markdown>{cp.narrative}</Markdown>
                ) : (
                  <>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Goals</p>
                    {cp.goals.map((g, i) => <div key={i} className="flex justify-between text-sm py-1 border-b border-border last:border-0"><span>{g.goal}</span><Badge>{g.status}</Badge></div>)}
                  </>
                )}
                <p className="text-xs text-muted-foreground mt-3">Created by {cp.created_by} · {(cp.created_at || "").slice(0, 10)}</p>
              </Card>
            ))}
          </div>
        )}

        {tab === "Claims" && (
          <Card className="overflow-hidden">
            <TableList
              cols={["Claim #", "Service Date", "Provider", "HCPCS", "Billed", "Paid", "Status"]}
              rows={m.claims.map((c) => [<span className="font-mono text-xs">{c.claim_number}</span>, c.service_date, c.provider_name, c.hcpcs, money(c.billed_amount), money(c.paid_amount), <Badge tone={c.status}>{c.status}</Badge>])}
              empty="No claims on file"
              icon={Receipt}
            />
          </Card>
        )}

        {tab === "Authorizations" && (
          <Card className="overflow-hidden">
            <TableList
              cols={["Auth #", "Service", "Type", "Units", "Status", "Reviewer"]}
              rows={m.um_requests.map((u) => [<span className="font-mono text-xs">{u.auth_number}</span>, u.service, u.request_type, u.units_requested, <Badge tone={u.status}>{u.status}</Badge>, u.reviewer])}
              empty="No authorizations on file"
              icon={ShieldCheck}
            />
          </Card>
        )}

        {tab === "Letters" && (
          <div className="space-y-4">
            {(!m.letters || m.letters.length === 0) ? (
              <Card className="p-10 text-center text-sm text-muted-foreground"><FileText className="h-6 w-6 mx-auto mb-2 opacity-40" />No letters generated yet. Use "Generate Letter" above.</Card>
            ) : m.letters.map((lt) => (
              <Card key={lt.id} className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-brand-700" />{lt.letter_type}</h4>
                  <span className="text-xs text-muted-foreground">{(lt.created_at || "").slice(0, 10)} · {lt.created_by}</span>
                </div>
                <div className="rounded-lg border border-border bg-surface p-4"><Markdown>{lt.content}</Markdown></div>
              </Card>
            ))}
          </div>
        )}

        {tab === "Messages" && (
          <Card className="p-5">
            <h3 className="font-display font-semibold mb-3">Secure Member Messaging</h3>
            <MessageThread memberId={id} height="h-96" />
          </Card>
        )}
      </div>

      <Modal open={letterOpen} onClose={() => setLetterOpen(false)} title="AI Letter Generator" wide>
        <div className="flex gap-3 items-end mb-4">
          <div className="flex-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Letter Type</label>
            <Select className="w-full mt-1.5" value={letterType} onChange={(e) => setLetterType(e.target.value)} data-testid="letter-type-select">
              {LETTER_TYPES.map((l) => <option key={l}>{l}</option>)}
            </Select>
          </div>
          <Button onClick={genLetter} disabled={letterBusy} data-testid="letter-generate-btn"><Sparkles className="h-4 w-4" />{letterBusy ? "Generating…" : "Generate"}</Button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-border bg-surface p-5">
          {letterBusy ? <Spinner label="Drafting letter…" /> : letter ? <Markdown>{letter}</Markdown> : <p className="text-sm text-muted-foreground">Select a letter type and generate an auto-drafted, plain-language member letter.</p>}
        </div>
      </Modal>
    </div>
  );
}

function TableList({ cols, rows, empty, icon: Icon }) {
  if (!rows.length) return <div className="p-10 text-center text-sm text-muted-foreground">{Icon && <Icon className="h-6 w-6 mx-auto mb-2 opacity-40" />}{empty}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">{cols.map((c) => <th key={c} className="px-4 py-3 font-semibold">{c}</th>)}</tr></thead>
        <tbody className="divide-y divide-border">
          {rows.map((r, i) => <tr key={i} className="hover:bg-muted transition-colors">{r.map((cell, j) => <td key={j} className="px-4 py-3 text-muted-foreground">{cell}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}
